import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { User } from '@features/users/models/user.model';
import {
  AMPLITUDE_TEMPLATE_NAMES,
  AmplitudeOtpErrorType,
  ConfigConstant,
} from '@shared/constants';

type Amplitude = typeof import('@amplitude/unified');

/**
 * Amplitude Analytics + Session Replay.
 *
 * Runs client-side only (this app has no server-side rendering target) and is
 * initialized exactly once per application lifecycle: `load` doubles as the
 * init guard, so concurrent callers share the same in-flight import.
 *
 * The SDK is pulled in with a dynamic `import()` so webpack emits it as a
 * separate chunk. A static import adds ~1.6 MB to the initial bundle, which
 * already sits above the budget configured in angular.json.
 *
 * Only the public API key lives here; the Amplitude secret key is server-side
 * only and must never be bundled into the front end.
 */
@Injectable({
  providedIn: 'root',
})
export class AmplitudeService {
  private sdk: Amplitude = null;
  private load: Promise<void> = null;

  /**
   * Last identified user, kept so the global properties can be attached to every
   * event. Null until login, which is expected for the "without user" events at
   * the top of the auth funnel.
   */
  private currentUser: User = null;

  /**
   * User properties that are not on the `User` object and have to be supplied by
   * whoever fetched them — `city` comes from the customer's locations. Merged
   * into every identify so they survive the later ones.
   */
  private extraProperties: Record<string, any> = {};

  /** Amplitude rejects user/device ids shorter than this. */
  private static readonly MIN_USER_ID_LENGTH = 5;

  /** ISO codes the data team wants reported under a different short name. */
  private static readonly COUNTRY_BY_ISO_CODE: Record<string, string> = {
    SA: 'KSA',
  };

  /** Short country name → full name, for the `country` user property. */
  private static readonly COUNTRY_NAME_BY_SHORT_NAME: Record<string, string> = {
    KSA: 'Saudi Arabia',
    AE: 'United Arab Emirates',
    KW: 'Kuwait',
    BH: 'Bahrain',
    OM: 'Oman',
    QA: 'Qatar',
    EG: 'Egypt',
  };

  /** Dial code → reported country, for the countries Madar operates in. */
  private static readonly COUNTRY_BY_DIAL_CODE: Record<string, string> = {
    '966': 'KSA',
    '971': 'AE',
    '965': 'KW',
    '973': 'BH',
    '968': 'OM',
    '974': 'QA',
    '20': 'EG',
  };

  init(user?: User): void {
    if (this.load || !environment.amplitudeApiKey || !this.isSessionStorageAccessible()) {
      return;
    }

    this.rememberUser(user);

    this.load = import('@amplitude/unified')
      .then((amplitude) => {
        amplitude.initAll(environment.amplitudeApiKey, {
          // Must match the region the Amplitude project is hosted in. If events
          // never reach the dashboard, this is the first thing to check.
          serverZone: 'EU',
          analytics: {
            // Autocapture off: only the events in `AmplitudeEvent` are wanted, and
            // autocapture would otherwise report every click, page view and form
            // interaction on top of them. `sessions` stays on — Session Replay
            // needs the session events to stitch a replay to a session.
            autocapture: {
              sessions: true,
              attribution: false,
              pageViews: false,
              formInteractions: false,
              fileDownloads: false,
              elementInteractions: false,
              frustrationInteractions: false,
              networkTracking: false,
              webVitals: false,
            },
          },
          sessionReplay: { sampleRate: 1 },
        });
        this.sdk = amplitude;
        this.applyUser(amplitude, user);
      })
      .catch((err) => console.error('Amplitude: failed to initialize', err));
  }

  /**
   * Attaches the signed-in user and their properties to the current session.
   * Called on login and again on profile update, so the properties in Amplitude
   * always reflect the latest values the backend returned.
   */
  identify(user: User): void {
    this.rememberUser(user);
    this.whenReady((amplitude) => this.applyUser(amplitude, user));
  }

  /**
   * Fires an event with the global properties merged in. Event-specific values
   * win, so a caller can override e.g. `page_url` for a deferred event.
   */
  track(eventName: string, properties?: Record<string, any>): void {
    const payload = { ...this.globalProperties(), ...(properties || {}) };
    this.whenReady((amplitude) => amplitude.track(eventName, payload));
  }

  /**
   * Adds user properties that only the caller can resolve (e.g. `city`, which
   * lives on the customer record rather than on the user) and re-identifies so
   * they reach Amplitude immediately.
   */
  setUserProperties(properties: Record<string, any>): void {
    this.extraProperties = { ...this.extraProperties, ...properties };

    if (this.currentUser) {
      this.identify(this.currentUser);
    }
  }

  /**
   * Device context as event properties. The same values are set as user
   * properties on identify, but the OTP screen event fires before the user is
   * identified, so that event has to carry them itself. `language` is not
   * repeated here — it is already a global property on every event.
   */
  deviceProperties(): Record<string, string> {
    return { device: this.device(), device_type: this.deviceType(), os: this.os() };
  }

  /** Reports a shipment template under the lowercase name the data team uses. */
  templateName(template: string): string {
    return AMPLITUDE_TEMPLATE_NAMES[template] || '';
  }

  /**
   * Maps a failed OTP response onto the agreed `error_type` values. The backend
   * returns human-readable messages, so this matches on their wording; anything
   * unrecognised is reported as `unknown` rather than guessed at.
   */
  otpErrorType(error: any): AmplitudeOtpErrorType {
    const reason = `${error?.errors?.join(' ') || ''} ${error?.message || ''}`.toLowerCase();

    if (/expire/.test(reason)) {
      return AmplitudeOtpErrorType.ExpiredOtp;
    }
    if (/too many|attempts|blocked|locked|limit/.test(reason)) {
      return AmplitudeOtpErrorType.TooManyAttempts;
    }
    if (/invalid|incorrect|wrong|not match|mismatch/.test(reason)) {
      return AmplitudeOtpErrorType.InvalidOtp;
    }
    return AmplitudeOtpErrorType.Unknown;
  }

  /** Clears the user id / device id, e.g. on logout. */
  reset(): void {
    this.whenReady((amplitude) => amplitude.reset());
    this.currentUser = null;
    // Must be dropped too: they belong to the user who just signed out, and the
    // next user on this tab would otherwise inherit them.
    this.extraProperties = {};
  }

  /**
   * Properties sent with every event. `page_url` is read at call time so it
   * reflects the page the event actually fired on, and the user fields are empty
   * for the pre-login events by design.
   */
  private globalProperties(): Record<string, any> {
    const user = this.currentUser;
    return {
      user_id: user ? user.code || `${user.id}` : '',
      user_phone_number: this.phoneNumber(user),
      account_type: user ? this.accountType(user) : '',
      language: this.language(),
      country: this.country(user),
      page_url: window.location.href,
    };
  }

  /**
   * Queues work until the SDK chunk has loaded, so events fired during startup
   * are not dropped. No-ops when Amplitude is disabled or failed to load.
   */
  private whenReady(fn: (amplitude: Amplitude) => void): void {
    if (!this.load) {
      return;
    }
    this.load.then(() => this.sdk && fn(this.sdk));
  }

  /**
   * The SDK's experiment plugin touches `sessionStorage` during setup, outside the
   * init promise, so a browser that blocks storage (embedded webviews, strict
   * privacy settings) throws an unhandled SecurityError. Skip Amplitude there.
   */
  private isSessionStorageAccessible(): boolean {
    try {
      return !!window.sessionStorage;
    } catch {
      return false;
    }
  }

  private applyUser(amplitude: Amplitude, user: User): void {
    if (!user?.id) {
      return;
    }

    // `code` is the user's Madar-facing identifier and the agreed distinct id;
    // the numeric id is only a fallback for accounts created before codes.
    amplitude.setUserId(this.amplitudeUserId(user));

    const identify = new amplitude.Identify();
    const properties = {
      user_id: user.code || `${user.id}`,
      full_name: (user.name || '').trim(),
      email: user.email || '',
      mobile_number: this.phoneNumber(user),
      company_name: user.firstName  || '',
      // User properties report the full country name (`Saudi Arabia`); the
      // per-event global property reports the short form (`KSA`).
      country: this.countryName(user),
      city: '',
      user_type: this.userType(user),
      account_state: user.status || '',
      default_shipment_template: this.defaultShipmentTemplate(user),
      // Device/session context, refreshed on every identify.
      device: this.device(),
      device_type: this.deviceType(),
      os: this.os(),
      language: this.language(),
      app: 'web-customer',
      appVersion: ConfigConstant.VERSION,
      // Anything supplied via `setUserProperties`, e.g. the city.
      ...this.extraProperties,
    };

    Object.keys(properties).forEach((key) => identify.set(key, properties[key]));
    amplitude.identify(identify);
  }

  /**
   * Registered company name. `title` is what the registration form collects
   * under "Company name", and the login flow refills it from the customer /
   * organization / fleet record before re-identifying the user.
   */

  /** The number as stored, e.g. `+966-000010527`. */
  private phoneNumber(user: User): string {
    return user?.mobileNumber || '';
  }

  /**
   * The Amplitude distinct id. Amplitude's HTTP API rejects a whole batch with
   * "Invalid id length for user_id or device_id" when the id is shorter than 5
   * characters, which short Madar codes are, so those are zero-padded. Codes of
   * 5+ characters — nearly all of them — are sent unchanged, and the `user_id`
   * property still carries the raw code for joining back to Madar.
   */
  private amplitudeUserId(user: User): string {
    const code = `${user.code || user.id}`;
    return code.padStart(AmplitudeService.MIN_USER_ID_LENGTH, '0');
  }

  private rememberUser(user: User): void {
    if (user?.id) {
      this.currentUser = user;
    }
  }

  /**
   * Whether the account finished company registration. `isVerified` is the only
   * signal the front end has for this; customers who chose "skip registration"
   * come back from the backend unverified.
   */
  private accountType(user: User): string {
    return user.isVerified ? 'verified' : 'unverified';
  }

  /**
   * Country of the user, as the short form the data team uses (e.g. `KSA`).
   *
   * Derived from the dial code of the mobile number: that is the country signal
   * the front end always has, whereas the stored user has no `country` object on
   * most accounts — the login response omits it — so it is only the fallback.
   */
  private country(user: User): string {
    // Digits only, so `+966-…` and `966…` both match. Longest code first, so a
    // short dial code can never shadow a longer one.
    const digits = (user?.mobileNumber || '').replace(/\D/g, '');
    const dialCode = Object.keys(AmplitudeService.COUNTRY_BY_DIAL_CODE)
      .sort((a, b) => b.length - a.length)
      .find((code) => digits.startsWith(code));

    if (dialCode) {
      return AmplitudeService.COUNTRY_BY_DIAL_CODE[dialCode];
    }

    const isoCode = user?.country?.isoCode;
    return isoCode ? AmplitudeService.COUNTRY_BY_ISO_CODE[isoCode] || isoCode : '';
  }

  /**
   * Full country name, e.g. `Saudi Arabia`. Resolved from the same dial-code
   * lookup as `country`, so both properties always agree; `user.country.name` is
   * only used when the number carries no recognised dial code.
   */
  private countryName(user: User): string {
    return (
      AmplitudeService.COUNTRY_NAME_BY_SHORT_NAME[this.country(user)] ||
      user?.country?.name ||
      ''
    );
  }

  /** Privilege level within the account; the first role is the primary one. */
  private userType(user: User): string {
    return (user.userRoles || [])[0] || 'guest';
  }

  /**
   * The shipment-creation template the user is set up with. Amplitude also
   * refreshes this on `shipment_created`, so this is only the starting value.
   */
  private defaultShipmentTemplate(user: User): string {
    return (
      (user.params || []).find((param) => param.creationTemplate)?.creationTemplate ||
      user.creationTemplates?.[0]?.type ||
      ''
    );
  }

  /** Device model, e.g. `iPhone`, `Android`, `Macintosh`. */
  private device(): string {
    const ua = navigator.userAgent;
    const match = /\((?:[^;)]*;\s*)?([^;)]+)/.exec(ua);
    return match?.[1]?.trim() || 'unknown';
  }

  private deviceType(): string {
    const ua = navigator.userAgent;
    if (/Mobi|Android|iPhone|iPod/i.test(ua)) {
      return 'mobile';
    }
    if (/Tablet|iPad/i.test(ua)) {
      return 'tablet';
    }
    return 'desktop';
  }

  /** OS name and version, e.g. `windows 10`, `ios 17.4`. */
  private os(): string {
    const ua = navigator.userAgent;
    const patterns: [RegExp, string][] = [
      [/Windows NT ([\d.]+)/, 'windows'],
      [/Android ([\d.]+)/, 'android'],
      [/(?:iPhone|iPad|iPod).*?OS ([\d_]+)/, 'ios'],
      [/Mac OS X ([\d_.]+)/, 'macos'],
    ];

    for (const [pattern, name] of patterns) {
      const match = pattern.exec(ua);
      if (match) {
        return `${name} ${match[1].replace(/_/g, '.')}`.trim();
      }
    }
    return /Linux/.test(ua) ? 'linux' : 'unknown';
  }

  /**
   * Read straight from the cookie LanguageService writes: injecting
   * LanguageService here would pull the layout/translation graph into a service
   * that has to be usable from the app-config bootstrap factory.
   */
  private language(): string {
    const match = new RegExp(`(?:^|;\\s*)${ConfigConstant.APP_LANGUAGE_COOKIE_KEY}=([^;]*)`).exec(
      document.cookie
    );
    return match ? decodeURIComponent(match[1]) : '';
  }
}
